import { useMemo, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { DatePicker, Select } from 'antd';
import { PageHeader } from '../components/yeni-design/primitives';
import { PeriodTabs, type DashboardPeriod } from '../components/PeriodTabs';
import { donemHesapla, donemAraligiMetni, type Donem } from '../components/satis-analiz/donem';
import { OzetSerit } from '../components/satis-analiz/OzetSerit';
import { KanalBlok } from '../components/satis-analiz/KanalBlok';
import { SubeBlok } from '../components/satis-analiz/SubeBlok';
import { ZamanBlok } from '../components/satis-analiz/ZamanBlok';
import { KategoriBlok } from '../components/satis-analiz/KategoriBlok';
import { DikkatBlok } from '../components/satis-analiz/DikkatBlok';
import { IsiHaritasiBlok } from '../components/satis-analiz/IsiHaritasiBlok';
import { SepetBlok } from '../components/satis-analiz/SepetBlok';
import { StokSatisBlok } from '../components/satis-analiz/StokSatisBlok';
import { KarKoprusuBlok } from '../components/satis-analiz/KarKoprusuBlok';
import { useSubeler } from '../components/satis-analiz/useSubeler';
import type { PanelScreen, ReportConfig } from '../types/panelConfig';

const { RangePicker } = DatePicker;

const VARSAYILAN_BLOKLAR = [
  'ozet', 'kanal', 'sube', 'zaman', 'isi', 'kategori', 'sepet', 'stok', 'kopru', 'dikkat',
];

interface SatisAnalizProps {
  screen: PanelScreen;
  reports: ReportConfig[];
}

export function SatisAnaliz({ screen, reports }: SatisAnalizProps) {
  const [period, setPeriod] = useState<DashboardPeriod>('aylik');
  const [ozelAralik, setOzelAralik] = useState<[Dayjs, Dayjs] | null>(null);
  // null = TUM subeler. Secim yalniz bu ekranda yasar (URL'ye ya da
  // localStorage'a yazilmaz) - "anlik" bir bakis, kalici bir tercih degil.
  const [sube, setSube] = useState<string | null>(null);

  const donem: Donem = useMemo(() => {
    if (period === 'ozel' && ozelAralik) {
      const bas = ozelAralik[0].format('YYYY-MM-DD');
      const bit = ozelAralik[1].format('YYYY-MM-DD');
      return { bas, bit, tekGun: bas === bit };
    }
    return donemHesapla(period);
  }, [period, ozelAralik]);

  const subeler = useSubeler(reports, donem);

  // Secili sube donem degisince listeden dusebilir (ör. o pencerede hic
  // satis yok). Secimi SESSIZCE tutmak "ATAKENT" yazip bos ekran gostermek
  // olurdu; listede yoksa TUM subelere donulur.
  const seciliSube = sube !== null && subeler.includes(sube) ? sube : null;

  const baslik = screen.config?.baslik?.trim() || screen.title;
  const bloklar = screen.config?.bloklar ?? VARSAYILAN_BLOKLAR;

  // Task 5-9 bloklari `ortak`'i kullanacak.
  const ortak = { reports, donem, sube: seciliSube, etiketler: screen.config?.etiketler ?? {} };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={baslik}
        actions={
          // 🔴 CANLIDA OLCULDU (2026-08-21, 390px iPhone): serit 548,9px
          // genisligindeydi ve `main` `overflow-x-hidden` tasidigi icin
          // KESILIYORDU - yatay kaydirma da yok. Sube secicinin sag kenari
          // 492,7px, tarih araligi ise tumden ekran disindaydi: telefonda
          // secici ERISILEMEZ durumdaydi. `flex-wrap` ile alt satira iner.
          <div
            className="flex flex-wrap items-center gap-2"
            data-testid="donem-secici"
          >
            <PeriodTabs value={period} onChange={setPeriod} />
            {/* Sube suzgeci: yalniz BIRDEN FAZLA sube varsa anlamli. Tek
                subeli firmada secenek kutusu gurultudur, hic basilmaz. */}
            {subeler.length > 1 && (
              <div data-testid="sube-secici">
                <Select
                  size="small"
                  style={{ minWidth: 140 }}
                  value={seciliSube ?? '__tum__'}
                  onChange={(v) => setSube(v === '__tum__' ? null : v)}
                  options={[
                    { value: '__tum__', label: 'Tüm şubeler' },
                    ...subeler.map((s) => ({ value: s, label: s })),
                  ]}
                />
              </div>
            )}
            {/* Secili donemin gercek araligi — yalniz "Ozel" degil HER
                sekmede gorunur; kullanici "Aylik" derken hangi tarihlere
                baktigini burada okur. */}
            <span className="text-[12px] text-ink-500 tnum" data-testid="donem-araligi">
              {donemAraligiMetni(donem)}
            </span>
            {period === 'ozel' && (
              // RangePicker `data-testid`'yi HEM baslangic HEM bitis input'una
              // yayar (iki eslesme -> getByTestId hata verir); testid'yi
              // sarmalayici tek div'e koy.
              <div data-testid="ozel-aralik">
                <RangePicker
                  value={ozelAralik}
                  onChange={(v) =>
                    setOzelAralik(v && v[0] && v[1] ? [v[0], v[1]] : null)
                  }
                  format="DD.MM.YYYY"
                  allowClear
                />
              </div>
            )}
          </div>
        }
      />
      {/* Izgara: eskiden bloklar TEK SUTUNDA alt alta diziliyordu (`flex-col`),
          yani ekran bir "dashboard" degil uzun bir liste idi. Iki sutuna gecince
          yan yana okunabilen bloklar (kanal + sube) ayni ekranda gorunuyor.
          `lg` altinda tek sutuna iner - dar ekranda iki sutun her ikisini de
          okunmaz yapardi.

          `items-start`: bir hucre komsusundan kisaysa GERILMEZ (grid varsayilani
          `stretch`, kisa karti bosluga esnetirdi).

          `min-w-0` SART: recharts'in ResponsiveContainer'i ebeveyninden genislik
          okur; grid hucresinin varsayilan `min-width:auto` degeri icerigin
          asgari genisligine kilitlenir ve grafik hucreyi TASIRARDI (Yerlice'de
          ayni kural `.satis-card { min-width: 0 }` olarak duruyor).

          `empty:hidden`: blok bileseni kendi bos-durum kuralindan `null`
          donerse sarmalayici BOS kalir; gizlenmezse izgarada gorunmez ama YER
          KAPLAYAN bir hucre birakir ve yanindaki blok yalniz basina asimetrik
          durur. */}
      <div
        className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start"
        data-testid="satis-analiz-izgara"
      >
        {bloklar.map((blok) => (
          <div key={blok} className={`min-w-0 empty:hidden ${BLOK_PAYI[blok] ?? TAM_GENISLIK}`}>
            {blokCiz(blok, ortak)}
          </div>
        ))}
      </div>
    </div>
  );
}

const TAM_GENISLIK = 'lg:col-span-2';

/**
 * Blogun izgarada kac sutun kaplayacagi. Burada OLMAYAN bir blok tam genislik
 * alir - bilinmeyen bir anahtar eklendiginde ekran bozulmaz, yalniz genis cizer.
 *
 * Yarim genislik yalnizca DAR icerikli bloklara verilir. Kategori bir TABLO
 * (siralanabilir kolonlar), Dikkat ise dort ayri liste tasir; ikisi de yarim
 * sutunda sikisirdi.
 */
const BLOK_PAYI: Record<string, string> = {
  ozet: TAM_GENISLIK,      // kendi icinde zaten 4 kolonluk KPI seridi
  kanal: '',               // yarim
  sube: '',                // yarim
  zaman: TAM_GENISLIK,     // zaman ekseni genislik ister
  isi: TAM_GENISLIK,       // 18 saat kolonu (canlida olculdu) yarim sutuna sigmaz
  kategori: TAM_GENISLIK,  // tablo
  sepet: TAM_GENISLIK,     // kendi icinde iki sutun: arama listesi + sepet dokumu
  stok: '',                // yarim
  kopru: '',               // yarim
  dikkat: TAM_GENISLIK,    // dort liste
};

/** Blok anahtarini bilesene cevirir. Bilinmeyen anahtar SESSIZCE atlanir ki
 *  ileride blok eklendiginde eski config'ler kirilmasin. */
function blokCiz(
  blok: string,
  ortak: { reports: ReportConfig[]; donem: Donem; sube: string | null },
) {
  switch (blok) {
    case 'ozet':
      return <OzetSerit reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
    case 'kanal':
      return <KanalBlok reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
    case 'sube':
      return <SubeBlok reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
    case 'zaman':
      return <ZamanBlok reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
    case 'kategori':
      return <KategoriBlok reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
    case 'isi':
      return <IsiHaritasiBlok reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
    // Sepet raporu donem parametresi ALMIYOR (onceden hesaplanmis tablo), o
    // yuzden `donem` gecilmiyor - gecmek "suzuluyor" izlenimi verirdi.
    case 'sepet':
      return <SepetBlok reports={ortak.reports} sube={ortak.sube} />;
    case 'stok':
      return <StokSatisBlok reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
    case 'kopru':
      return <KarKoprusuBlok reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
    case 'dikkat':
      return <DikkatBlok reports={ortak.reports} donem={ortak.donem} sube={ortak.sube} />;
    default:
      return null;
  }
}
